import React, { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Clock, Eye, MapPin, Phone, Hash, GraduationCap, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError, isSuperAdmin } from "../../lib/api";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const STATUS = {
  en_revision: { label: "En revisión", cls: "bg-[#FBF0CE] text-[#B9860B]", icon: Clock },
  valida: { label: "Válida", cls: "bg-[#E2F6EB] text-[#22A860]", icon: CheckCircle2 },
  rechazada: { label: "Rechazada", cls: "bg-[#FCE4E4] text-[#E23B3B]", icon: XCircle },
};

const REASONS = [
  { key: "calidad", label: "No cumple con la calidad requerida, vuelva a enviar." },
  { key: "no_corresponde", label: "El comentario no corresponde a la reflexión del día, vuelva a enviar." },
  { key: "ambiguo", label: "Comentario ambiguo sobre la reflexión, vuelva a enviar." },
];

const FILTERS = [
  { key: "all", label: "Todas" },
  { key: "en_revision", label: "En revisión" },
  { key: "valida", label: "Válidas" },
  { key: "rechazada", label: "Rechazadas" },
];

export default function EvidenciasAdmin() {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ total: 0, pages: 1 });
  const [active, setActive] = useState(null);
  const [imgData, setImgData] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [reason, setReason] = useState("calidad");
  const [saving, setSaving] = useState(false);
  const superAdmin = isSuperAdmin();
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = (f = filter, p = page) => {
    setLoading(true);
    adminApi.listEvidencias(f, p).then((d) => {
      setItems(d.items || []);
      setPageInfo({ total: d.total || 0, pages: d.pages || 1 });
    }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(filter, page); /* eslint-disable-next-line */ }, [filter, page]);

  const changeFilter = (key) => { setFilter(key); setPage(1); };

  const openReview = async (ev) => {
    setActive(ev); setFeedback(ev.feedback || ""); setReason(ev.rejection_reason || "calidad");
    setImgData(null);
    if (ev.has_imagen) {
      setImgLoading(true);
      try { setImgData(await adminApi.evidenciaImagen(ev.id)); }
      catch (e) { /* imagen opcional */ }
      finally { setImgLoading(false); }
    }
  };

  const deleteOne = async (ev) => {
    if (!window.confirm(`¿Eliminar esta evidencia de ${ev.user_name || ev.user_email}? No afectará sus puntos.`)) return;
    try {
      await adminApi.deleteEvidencia(ev.id);
      toast.success("Evidencia eliminada.");
      if (active?.id === ev.id) setActive(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteBulk = async () => {
    setDeleting(true);
    try {
      const payload = filter === "all" ? { all: true } : { all: true, status: filter };
      const r = await adminApi.deleteEvidenciasBulk(payload);
      toast.success(`${r.deleted} evidencia(s) eliminada(s). Los puntos de los usuarios no se modificaron.`);
      setConfirmBulk(false);
      setPage(1);
      load(filter, 1);
    } catch (e) { toast.error(apiError(e)); } finally { setDeleting(false); }
  };

  const review = async (status) => {
    setSaving(true);
    try {
      const payload = { status, feedback };
      if (status === "rechazada") payload.rejection_reason = reason;
      await adminApi.reviewEvidencia(active.id, payload);
      toast.success(status === "valida" ? "Evidencia validada" : "Evidencia rechazada");
      setActive(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <h1 className="text-3xl font-extrabold text-[#0A1C30]">Evidencias de Reflexiones</h1>
      <p className="text-[#6B7688] mt-1">Repositorio de evidencias recibidas. Valida o rechaza con retroalimentación.</p>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => changeFilter(f.key)} className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${filter === f.key ? "bg-navy text-white" : "bg-white text-[#0A1C30] ring-1 ring-black/5 hover:ring-[var(--gold)]"}`}>
            {f.label}
          </button>
        ))}
        {superAdmin && items.length > 0 && (
          <button data-testid="evid-delete-all" onClick={() => setConfirmBulk(true)} className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#FCE4E4] text-[#C0392B] font-bold px-4 py-2 text-sm hover:bg-[#f7d3d3] transition-colors">
            <Trash2 className="w-4 h-4" /> Eliminar {filter === "all" ? "todas" : "las de esta vista"}
          </button>
        )}
      </div>

      <div className="mt-6 bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-black/5 bg-[#FAFAFA]">
                {["Usuario", "Programa", "Referencia", "País / Ciudad", "WhatsApp", "Fecha", "Estado"].map((h) => (
                  <th key={h} className="px-4 py-3.5 text-xs font-extrabold tracking-wide text-[#6B7688] uppercase whitespace-nowrap">{h}</th>
                ))}
                <th className="px-4 py-3.5 text-right text-xs font-extrabold tracking-wide text-[#6B7688] uppercase">Revisar</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#6B7688]">Cargando...</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#6B7688]">Sin evidencias.</td></tr>}
              {items.map((ev) => {
                const st = STATUS[ev.status] || STATUS.en_revision;
                return (
                  <tr key={ev.id} className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA]">
                    <td className="px-4 py-4 text-sm font-bold text-[#0A1C30] whitespace-nowrap">{ev.user_name || ev.user_email}</td>
                    <td className="px-4 py-4 text-sm text-[#2A3446] whitespace-nowrap">{ev.programa || "—"}</td>
                    <td className="px-4 py-4 text-sm text-[#2A3446]">{ev.referencia || "—"}</td>
                    <td className="px-4 py-4 text-sm text-[#2A3446] whitespace-nowrap">{ev.pais || "—"}{ev.ciudad ? ` · ${ev.ciudad}` : ""}</td>
                    <td className="px-4 py-4 text-sm text-[#2A3446] whitespace-nowrap">{ev.whatsapp || "—"}</td>
                    <td className="px-4 py-4 text-sm text-[#6B7688] whitespace-nowrap">{ev.reflexion_fecha}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${st.cls}`}>
                        <st.icon className="w-3.5 h-3.5" /> {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button onClick={() => openReview(ev)} className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A1C30] text-white px-3 py-2 text-xs font-bold hover:bg-[#0E2740] transition-colors">
                          <Eye className="w-4 h-4" /> Ver
                        </button>
                        {superAdmin && (
                          <button data-testid={`evid-del-${ev.id}`} onClick={() => deleteOne(ev)} title="Eliminar evidencia" className="inline-flex items-center justify-center rounded-lg bg-[#FCE4E4] text-[#C0392B] p-2 hover:bg-[#f7d3d3] transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {pageInfo.pages > 1 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[#6B7688]" data-testid="evid-page-info">Página {page} de {pageInfo.pages} · {pageInfo.total} evidencias</p>
          <div className="flex gap-2">
            <button data-testid="evid-prev" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg bg-white ring-1 ring-black/5 text-[#0A1C30] font-bold px-4 py-2 text-sm disabled:opacity-40">Anterior</button>
            <button data-testid="evid-next" disabled={page >= pageInfo.pages || loading} onClick={() => setPage((p) => Math.min(pageInfo.pages, p + 1))} className="rounded-lg bg-navy text-white font-bold px-4 py-2 text-sm disabled:opacity-40">Siguiente</button>
          </div>
        </div>
      )}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold text-[#0A1C30]">Revisar evidencia</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-4 py-1">
              {/* User details */}
              <div className="rounded-xl bg-[#F4F6F9] p-4">
                <p className="font-extrabold text-[#0A1C30]">{active.user_name}</p>
                <p className="text-sm text-[#6B7688]">{active.user_email}</p>
                <div className="mt-2 grid grid-cols-2 gap-y-1.5 text-sm text-[#2A3446]">
                  <span className="inline-flex items-center gap-1.5"><GraduationCap className="w-4 h-4 text-[#9AA4B4]" /> {active.programa || "—"}</span>
                  <span className="inline-flex items-center gap-1.5"><Hash className="w-4 h-4 text-[#9AA4B4]" /> {active.referencia || "—"}</span>
                  <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#9AA4B4]" /> {active.pais || "—"}{active.ciudad ? `, ${active.ciudad}` : ""}</span>
                  <span className="inline-flex items-center gap-1.5"><Phone className="w-4 h-4 text-[#9AA4B4]" /> {active.whatsapp || "—"}</span>
                </div>
              </div>

              {/* Reflexion snapshot */}
              <div>
                <Label className="text-xs font-extrabold tracking-wide text-[#B99B2E] uppercase">Reflexión ({active.reflexion_fecha})</Label>
                {active.reflexion_snapshot?.titulo && <p className="font-bold text-[#0A1C30] mt-1">{active.reflexion_snapshot.titulo}</p>}
                {imgLoading && <div className="mt-1 flex items-center gap-2 text-sm text-[#6B7688]"><Loader2 className="w-4 h-4 animate-spin" /> Cargando imagen…</div>}
                {imgData?.reflexion_snapshot_imagen_url && (
                  <img src={imgData.reflexion_snapshot_imagen_url} alt="reflexion" className="mt-1 max-h-40 rounded-lg object-contain bg-[#F4F6F9]" />
                )}
                {active.reflexion_snapshot?.texto && <p className="text-sm text-[#6B7688] mt-1">{active.reflexion_snapshot.texto}</p>}
              </div>

              {/* Imagen de la evidencia del usuario (Campus / Graduación) */}
              {imgData?.imagen_url && (
                <div>
                  <Label className="text-xs font-extrabold tracking-wide text-[#B99B2E] uppercase">Imagen enviada</Label>
                  <img src={imgData.imagen_url} alt="evidencia" className="mt-1 max-h-64 rounded-lg object-contain bg-[#F4F6F9] w-full" />
                </div>
              )}

              {/* Detalle de la Hoja de Compromisos (para revisión del staff) */}
              {Array.isArray(active.compromiso_detalle) && active.compromiso_detalle.length > 0 && (
                <div data-testid="evid-compromiso-detalle">
                  <Label className="text-xs font-extrabold tracking-wide text-[#B99B2E] uppercase">Actividades cumplidas (Hoja de Compromisos)</Label>
                  <div className="mt-1.5 space-y-3">
                    {active.compromiso_detalle.map((sec, si) => (
                      <div key={si} className="rounded-xl bg-[#F4F6F9] p-3">
                        <p className="text-sm font-extrabold text-[#0A1C30] mb-1.5">{sec.seccion}</p>
                        <div className="space-y-1">
                          {(sec.actividades || []).map((a, ai) => {
                            const ok = (a.cumplidas || 0) >= (a.requeridas || 0);
                            return (
                              <div key={ai} className="flex items-center justify-between gap-3 text-sm">
                                <span className="text-[#2A3446] flex-1">{a.nombre}</span>
                                <span className={`inline-flex items-center gap-1 font-bold ${ok ? "text-[#22A860]" : "text-[#E23B3B]"}`}>
                                  {ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {a.cumplidas}/{a.requeridas}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Comment */}
              <div>
                <Label className="text-xs font-extrabold tracking-wide text-[#B99B2E] uppercase">Comentario del usuario</Label>
                <p className="mt-1 text-[#2A3446] bg-white ring-1 ring-black/5 rounded-xl p-3">{active.comentario}</p>
              </div>

              {/* Feedback */}
              <div>
                <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Retroalimentación (opcional)</Label>
                <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Comentario para el usuario..." className="min-h-[80px]" />
              </div>

              {/* Reject reason */}
              <div>
                <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Motivo (si rechazas)</Label>
                <div className="space-y-2">
                  {REASONS.map((r) => (
                    <label key={r.key} className="flex items-start gap-2 rounded-lg bg-[#F4F6F9] px-3 py-2.5 cursor-pointer text-sm">
                      <input type="radio" name="reason" checked={reason === r.key} onChange={() => setReason(r.key)} className="accent-[#E23B3B] mt-0.5 w-4 h-4" />
                      <span className="text-[#2A3446]">{r.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <button onClick={() => review("rechazada")} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#FCE4E4] text-[#E23B3B] font-extrabold py-3 hover:bg-[#f9d3d3] transition-colors disabled:opacity-60">
                  <XCircle className="w-5 h-5" /> Rechazar
                </button>
                <button onClick={() => review("valida")} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#22A860] text-white font-extrabold py-3 hover:bg-[#1c9152] transition-colors disabled:opacity-60">
                  <CheckCircle2 className="w-5 h-5" /> Validar
                </button>
              </div>
              {superAdmin && (
                <button data-testid="evid-del-dialog" onClick={() => deleteOne(active)} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white ring-1 ring-[#E23B3B]/30 text-[#C0392B] font-bold py-2.5 hover:bg-[#FCE4E4] transition-colors">
                  <Trash2 className="w-4 h-4" /> Eliminar esta evidencia
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmación de borrado en grupo */}
      <Dialog open={confirmBulk} onOpenChange={(o) => !o && setConfirmBulk(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-xl font-extrabold text-[#0A1C30]">Eliminar evidencias</DialogTitle></DialogHeader>
          <p className="text-sm text-[#2A3446]">
            Se eliminarán <b>{filter === "all" ? "TODAS las evidencias" : `todas las evidencias con estado "${FILTERS.find((f) => f.key === filter)?.label}"`}</b> ({pageInfo.total} en total). Esta acción libera espacio y <b>no afecta los puntos de graduación ni las medallas</b> de ningún usuario.
          </p>
          <DialogFooter className="gap-2">
            <button onClick={() => setConfirmBulk(false)} className="rounded-xl bg-[#F4F6F9] text-[#0A1C30] font-bold px-5 py-2.5">Cancelar</button>
            <button data-testid="evid-delete-all-confirm" onClick={deleteBulk} disabled={deleting} className="inline-flex items-center gap-2 rounded-xl bg-[#C0392B] text-white font-extrabold px-5 py-2.5 disabled:opacity-60">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Eliminar definitivamente
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
